import React, { CSSProperties, ReactNode, useEffect, useMemo, useState } from "react";

type Transition = {
  duration?: number;
  delay?: number;
  ease?: string;
};

type AnimationProperties = {
  x?: number | string;
  y?: number | string;
  scale?: number;
  rotate?: number;
  opacity?: number;
  [key: string]: any;
};

type MotionProps<T extends HTMLElement> = React.HTMLAttributes<T> & {
  initial?: CSSProperties & AnimationProperties;
  animate?: CSSProperties & AnimationProperties;
  exit?: CSSProperties & AnimationProperties;
  transition?: Transition;
  children?: ReactNode;
  layout?: string;
};

function buildTransition(transition?: Transition): string | undefined {
  if (!transition) return undefined;
  const duration = transition.duration ?? 0.4;
  const ease = transition.ease ?? "ease";
  const delay = transition.delay ?? 0;
  return `all ${duration}s ${ease} ${delay}s`;
}

function convertToCSS(props?: CSSProperties & AnimationProperties): CSSProperties {
  if (!props) return {};
  
  const { x, y, scale, rotate, ...rest } = props;
  const transforms: string[] = [];
  
  if (x !== undefined) transforms.push(`translateX(${typeof x === 'number' ? `${x}px` : x})`);
  if (y !== undefined) transforms.push(`translateY(${typeof y === 'number' ? `${y}px` : y})`);
  if (scale !== undefined) transforms.push(`scale(${scale})`);
  if (rotate !== undefined) transforms.push(`rotate(${rotate}deg)`);
  
  const result: CSSProperties = { ...rest };
  if (transforms.length > 0) {
    result.transform = transforms.join(' ');
  }
  
  return result;
}

function createMotionComponent<T extends HTMLElement>(
  Component: keyof JSX.IntrinsicElements
) {
  return React.forwardRef<T, MotionProps<T>>(
    ({ initial, animate, exit, transition, style, children, layout, ...rest }, ref) => {
      const [renderStyle, setRenderStyle] = useState<CSSProperties>(() => ({
        ...convertToCSS(initial),
        ...(style ?? {}),
        transition: buildTransition(transition),
      }));

      const targetStyle = useMemo(() => ({
        ...convertToCSS(animate),
        ...(style ?? {}),
        transition: buildTransition(transition),
      }), [animate, style, transition]);

      useEffect(() => {
        const frame = requestAnimationFrame(() => setRenderStyle(targetStyle));
        return () => cancelAnimationFrame(frame);
      }, [targetStyle]);

      useEffect(() => {
        return () => {
          if (exit) {
            setRenderStyle({
              ...convertToCSS(exit),
              ...(style ?? {}),
              transition: buildTransition(transition),
            });
          }
        };
      }, [exit, style, transition]);

      const Comp = Component as any;
      return (
        <Comp ref={ref} style={renderStyle} {...rest}>
          {children}
        </Comp>
      );
    }
  );
}

export const motion = {
  div: createMotionComponent<HTMLDivElement>("div"),
  section: createMotionComponent<HTMLElement>("section"),
};

export function AnimatePresence({ 
  children, 
  initial 
}: { 
  children: ReactNode;
  initial?: boolean;
}) {
  return <>{children}</>;
}
